import React, { useState, useEffect, useCallback } from 'react';
import useSWR from 'swr';
import { Input } from './ui/input';
import OnOffToggle from './OnOffToggle';
import { Label } from './ui/label';
import ActionButton from './ui/action-button';
import { Star, CheckCircle, Mail, ChevronDown, ChevronUp, Eye } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import TabPageLayout from './TabPageLayout';
import FormSection from './FormSection';

// SWR fetcher
const fetcher = async (url) => {
  const response = await api.get(url);
  return response.data;
};

const ReviewsTab = () => {
  const [saving, setSaving] = useState(false);
  
  // Step 1: Review Button on Tickets
  const [reviewLinkEnabled, setReviewLinkEnabled] = useState(false);
  const [reviewLinkUrl, setReviewLinkUrl] = useState('');
  
  // Step 2: Thank You Email Settings (simplified - 24h is the default)
  const [thankYouEmailEnabled, setThankYouEmailEnabled] = useState(false);
  const [thankYouEmailIncludeReview, setThankYouEmailIncludeReview] = useState(true);
  
  // Email preview toggle
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  
  // Track original values for unsaved changes detection
  const [originalValues, setOriginalValues] = useState({
    reviewLinkEnabled: false,
    reviewLinkUrl: '',
    thankYouEmailEnabled: false,
    thankYouEmailIncludeReview: true
  });
  
  // Organization name and owner for email preview
  const [orgName, setOrgName] = useState('Your Company');
  const [ownerName, setOwnerName] = useState('Ryan');
  
  // Fetch organization settings with SWR
  const orgId = typeof window !== 'undefined' ? localStorage.getItem('organizationId') : null;
  const { data: orgData, isLoading: loading } = useSWR(
    orgId ? `/organizations/${orgId}` : null,
    fetcher,
    { 
      revalidateOnFocus: true,
      dedupingInterval: 5000
    }
  );
  
  // Update local state when data is fetched
  useEffect(() => {
    if (orgData) {
      const values = {
        reviewLinkEnabled: orgData.reviewLinkEnabled || false,
        reviewLinkUrl: orgData.reviewLinkUrl || '',
        thankYouEmailEnabled: orgData.thankYouEmailEnabled || false,
        thankYouEmailIncludeReview: orgData.thankYouEmailIncludeReview !== false
      };
      
      setReviewLinkEnabled(values.reviewLinkEnabled);
      setReviewLinkUrl(values.reviewLinkUrl);
      setThankYouEmailEnabled(values.thankYouEmailEnabled);
      setThankYouEmailIncludeReview(values.thankYouEmailIncludeReview);
      setOriginalValues(values);
      setOrgName(orgData.name || 'Your Company');
      // Get owner's first name for email preview
      const fullOwnerName = orgData.ownerName || 'Ryan';
      setOwnerName(fullOwnerName.split(' ')[0]); // Just first name
    }
  }, [orgData]);
  
  const hasUnsavedChanges = 
    reviewLinkEnabled !== originalValues.reviewLinkEnabled ||
    reviewLinkUrl !== originalValues.reviewLinkUrl ||
    thankYouEmailEnabled !== originalValues.thankYouEmailEnabled ||
    thankYouEmailIncludeReview !== originalValues.thankYouEmailIncludeReview;

  const handleSave = async () => {
    // Validate: if review button on tickets enabled, URL is required
    if (reviewLinkEnabled && !reviewLinkUrl) {
      toast.error('Please enter a review link URL to show on tickets');
      return;
    }
    
    // Validate: if include review in email is enabled, URL must be set
    if (thankYouEmailEnabled && thankYouEmailIncludeReview && !reviewLinkUrl) {
      toast.error('Please enter a review link URL to include in emails');
      return;
    }

    // Validate URL format if provided
    if (reviewLinkUrl) {
      try {
        new URL(reviewLinkUrl);
      } catch {
        toast.error('Please enter a valid URL (e.g., https://g.page/your-business/review)');
        return;
      }
    }

    setSaving(true);
    try {
      const orgId = localStorage.getItem('organizationId');
      await api.put(`/organizations/${orgId}`, {
        reviewLinkEnabled: reviewLinkEnabled && !!reviewLinkUrl, // Only enable if URL exists
        reviewLinkUrl,
        thankYouEmailEnabled,
        thankYouEmailDelayHours: 24, // Always 24 hours - simplified
        thankYouEmailIncludeReview: thankYouEmailIncludeReview && !!reviewLinkUrl // Only include if URL exists
      });

      toast.success('Settings saved!');
      
      // Update original values after successful save
      setOriginalValues({
        reviewLinkEnabled: reviewLinkEnabled && !!reviewLinkUrl,
        reviewLinkUrl,
        thankYouEmailEnabled,
        thankYouEmailIncludeReview: thankYouEmailIncludeReview && !!reviewLinkUrl
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <TabPageLayout
      title="Reviews & Follow-Up"
      subtitle="Configure review links and automated thank you emails"
      loading={loading}
      actions={
        <div className="flex items-center gap-3">
          {hasUnsavedChanges && (
            <span className="text-xs sm:text-sm text-amber-600 font-medium animate-pulse">
              ⚠️ Unsaved changes
            </span>
          )}
          <ActionButton
            onClick={handleSave}
            loading={saving}
            icon={CheckCircle}
            className={hasUnsavedChanges ? 'ring-1 ring-amber-400 ring-offset-1' : ''}
          >
            {hasUnsavedChanges ? 'Save Changes' : 'Save Settings'}
          </ActionButton>
        </div>
      }
    >
      <div className="space-y-6">
        {/* STEP 1: Review Link Configuration */}
        <FormSection
          stepNumber={1}
          title="Review Link"
          description="Set up your review link for tickets and emails"
          color="orange"
        >
          <div className="space-y-4">
            {/* Review Link URL - always visible */}
            <div>
              <Label className="text-sm sm:text-base font-semibold text-gray-900 mb-2 block">
                Review Link URL
              </Label>
              <Input
                value={reviewLinkUrl}
                onChange={(e) => setReviewLinkUrl(e.target.value)}
                placeholder="https://g.page/your-business/review"
                className="text-sm sm:text-base h-11"
              />
              <p className="text-xs sm:text-sm text-gray-500 mt-2">
                📍 <strong>Popular:</strong> Google Reviews, Yelp, TripAdvisor, Facebook
              </p>
            </div>
            
            {/* Enable Review Button on Tickets Toggle - only show if URL is set */}
            {reviewLinkUrl && (
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div>
                  <Label className="text-sm sm:text-base font-semibold text-gray-900">Show on Booking Tickets</Label>
                  <p className="text-xs sm:text-sm text-gray-600 mt-1">
                    Display a &ldquo;Leave a Review&rdquo; button on customer tickets
                  </p>
                </div>
                <OnOffToggle
                  value={reviewLinkEnabled}
                  onChange={setReviewLinkEnabled}
                />
              </div>
            )}
          </div>
        </FormSection>

        {/* STEP 2: Preview - only show when enabled with URL */}
        {reviewLinkEnabled && reviewLinkUrl && (
          <FormSection
            stepNumber={2}
            title="Button Preview"
            description="See how the review button looks on customer tickets"
            color="yellow"
          >
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
              <p className="text-xs sm:text-sm text-gray-600 mb-4 text-center">
                This is how customers will see the review button:
              </p>
              
              {/* Preview Button */}
              <a
                href={reviewLinkUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2 shadow-lg transition-all block text-sm sm:text-base h-11"
              >
                <Star className="w-5 h-5 fill-white" />
                Leave a Review
              </a>
              
              <p className="text-xs text-gray-500 mt-3 text-center">
                Click to test your link
              </p>
            </div>
          </FormSection>
        )}

        {/* STEP 3: Thank You Email Settings */}
        <FormSection
          stepNumber={reviewLinkEnabled && reviewLinkUrl ? 3 : 2}
          title="Thank You Emails"
          description="Automatically email customers 24 hours after their booking"
          color="green"
        >
          <div className="space-y-4">
            {/* Enable Thank You Emails Toggle */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-green-600 mt-0.5" />
                <div>
                  <Label className="text-sm sm:text-base font-semibold text-gray-900">Send Thank You Email</Label>
                  <p className="text-xs sm:text-sm text-gray-600 mt-1">
                    Email customers 24 hours after their booking ends
                  </p>
                </div>
              </div>
              <OnOffToggle
                value={thankYouEmailEnabled}
                onChange={setThankYouEmailEnabled}
              />
            </div>
            
            {/* Include Review Link Toggle - only show when thank you emails enabled AND there's a URL */}
            {thankYouEmailEnabled && reviewLinkUrl && (
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-start gap-3">
                  <Star className="w-5 h-5 text-yellow-500 mt-0.5" />
                  <div>
                    <Label className="text-sm sm:text-base font-semibold text-gray-900">
                      Include Review Link
                    </Label>
                    <p className="text-xs sm:text-sm text-gray-600 mt-1">
                      Add your review button to the thank you email
                    </p>
                  </div>
                </div>
                <OnOffToggle
                  value={thankYouEmailIncludeReview}
                  onChange={setThankYouEmailIncludeReview}
                />
              </div>
            )}
            
            {/* Email Preview - Collapsible */}
            {thankYouEmailEnabled && (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setShowEmailPreview(!showEmailPreview)}
                  className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                >
                  <div className="flex items-center gap-2">
                    <Eye className="w-4 h-4 text-gray-500" />
                    <span className="text-sm font-medium text-gray-700">Preview Email</span>
                  </div>
                  {showEmailPreview ? (
                    <ChevronUp className="w-4 h-4 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-500" />
                  )}
                </button>
                
                {showEmailPreview && (
                  <div className="p-4 bg-gray-100 border-t border-gray-200">
                    {/* Real Email Template Preview */}
                    <div 
                      className="max-w-lg mx-auto"
                      style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif" }}
                    >
                      <div className="bg-white rounded-xl overflow-hidden shadow-lg">
                        {/* Header */}
                        <div 
                          className="px-6 py-8 text-center"
                          style={{ background: "linear-gradient(135deg, #10b981 0%, #10b981dd 100%)" }}
                        >
                          <h1 className="text-white text-2xl font-bold m-0">Thank You! 🎉</h1>
                        </div>
                        
                        {/* Message Content */}
                        <div className="p-6">
                          <p className="text-lg text-gray-900 mb-4">Hi {ownerName},</p>
                          
                          <p className="text-base text-gray-600 mb-5">
                            Thank you for choosing <strong>{orgName}</strong>! We hope you had an amazing experience.
                          </p>
                          
                          {/* Review Section - only if enabled */}
                          {thankYouEmailIncludeReview && reviewLinkUrl && (
                            <div 
                              className="rounded-xl p-6 my-6 text-center"
                              style={{ background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)" }}
                            >
                              <p className="text-base font-semibold mb-3" style={{ color: "#92400e" }}>
                                ⭐ <strong>Enjoyed your experience?</strong>
                              </p>
                              <p className="text-sm mb-4" style={{ color: "#92400e" }}>
                                We&apos;d love to hear from you! Your review helps others discover us.
                              </p>
                              <span 
                                className="inline-block px-7 py-3 rounded-lg font-semibold text-base text-white"
                                style={{ background: "#10b981" }}
                              >
                                Leave a Review →
                              </span>
                            </div>
                          )}
                          
                          <p className="text-base text-gray-600 my-5">
                            We&apos;d love to see you again soon!
                          </p>
                          
                          <p className="text-base text-gray-900 m-0">
                            Best regards,<br />
                            <strong>The {orgName} Team</strong>
                          </p>
                        </div>
                        
                        {/* Footer */}
                        <div className="bg-gray-50 px-5 py-4 text-center border-t border-gray-200">
                          <p className="m-0 text-xs text-gray-400">
                            Powered by {orgName}
                          </p>
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 text-center mt-4">
                      📧 Each email is personalized with your customer&apos;s name
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </FormSection>
      </div>
    </TabPageLayout>
  );
};

export default ReviewsTab;
